import { computed, ref, unref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import camelcaseKeys from 'camelcase-keys';
import ImovelAPI from 'dashboard/api/imoveis';
import PortalIntegrationsAPI from 'dashboard/api/portalIntegrations';
import { useAlert } from 'dashboard/composables';
import {
  DEFAULT_PORTAL,
  PORTAL_CONNECTION_LABELS,
  PORTAL_OPTIONS,
  PORTAL_SYNC_ACTION_LABELS,
  PORTAL_SYNC_STATUS_LABELS,
} from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  confirmSensitiveAction,
  formatDateTime,
  normalizePayload,
  portalActionErrorMessage,
} from '../utils/imovelShowFormatters';

/**
 * Integração com portais: status, preflight, histórico e ações sensíveis.
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {import('vue').Ref<object>} options.currentListing
 * @param {import('vue').Ref<string>} options.lifecycleReason
 * @param {import('vue').Ref<string>} options.activeSection
 * @param {import('vue').Ref<string|number>} options.sectionAccountId
 * @param {Function} options.refreshImovel
 */
export function useImovelPortalPublication({
  imovel,
  currentListing,
  lifecycleReason,
  activeSection,
  sectionAccountId,
  refreshImovel,
}) {
  const route = useRoute();
  const router = useRouter();

  const selectedPortal = ref(
    String(route.query.portal || route.query.portal_id || DEFAULT_PORTAL)
  );
  const portalStatus = ref(null);
  const portalPreflight = ref(null);
  const portalSyncs = ref([]);
  const portalLoading = ref(false);
  const portalActionLoading = ref('');
  const portalError = ref('');
  const portalPermissionDenied = ref(false);
  const portalSuccessMessage = ref('');

  const normalizedPortalSyncs = computed(() => {
    let records = portalSyncs.value;
    if (!records.length && unref(currentListing)?.portalStatuses?.length) {
      records = unref(currentListing).portalStatuses;
    }
    if (!records.length) {
      records = unref(imovel)?.portalSyncs || [];
    }

    return records.map(sync => camelcaseKeys(sync || {}, { deep: true }));
  });
  const selectedPortalOption = computed(
    () =>
      PORTAL_OPTIONS.find(option => option.value === selectedPortal.value) ||
      PORTAL_OPTIONS[0]
  );
  const selectedPortalLabel = computed(
    () =>
      selectedPortalOption.value?.label || selectedPortal.value.toUpperCase()
  );
  const selectedPortalSync = computed(
    () =>
      normalizedPortalSyncs.value.find(
        sync => sync.portal === selectedPortal.value
      ) || null
  );
  const olxSync = computed(() => selectedPortalSync.value);
  const olxConfigured = computed(() => Boolean(portalStatus.value?.configured));
  const olxConnected = computed(() => Boolean(portalStatus.value?.connected));
  const portalPreflightBlockers = computed(
    () => portalPreflight.value?.blockers || []
  );
  const portalPreflightWarnings = computed(
    () => portalPreflight.value?.warnings || []
  );
  const portalPreflightBlockerCodes = computed(() =>
    portalPreflightBlockers.value.map(blocker => blocker.code).filter(Boolean)
  );
  const portalCredentialSettings = computed(
    () => portalPreflight.value?.hook?.settings || {}
  );
  const portalMissingCredentialGroups = computed(
    () => portalCredentialSettings.value?.missingRequiredGroups || []
  );
  const portalPresentCredentialGroups = computed(
    () => portalCredentialSettings.value?.presentGroups || []
  );
  const portalManualConfigurationRows = computed(() =>
    [
      {
        label: 'Portal',
        value: selectedPortalLabel.value,
      },
      {
        label: 'Autenticação',
        value:
          portalStatus.value?.authMode ||
          portalPreflight.value?.adapter?.authMode ||
          'Não informada',
      },
      {
        label: 'Credenciais presentes',
        value: portalPresentCredentialGroups.value.length
          ? portalPresentCredentialGroups.value.join(', ')
          : 'Nenhum grupo confirmado',
      },
      {
        label: 'Credenciais pendentes',
        value: portalMissingCredentialGroups.value.length
          ? portalMissingCredentialGroups.value.join(', ')
          : 'Sem pendências obrigatórias',
      },
      {
        label: 'Redação de segredos',
        value:
          portalPreflight.value?.redaction?.secretValues ||
          portalPreflight.value?.redaction?.providerPayloads ||
          'Não verificada',
      },
    ].filter(row => row.value)
  );
  const portalRuntimeState = computed(() => {
    if (
      portalPreflightBlockerCodes.value.includes('hook_secret_invalid') ||
      portalPreflightBlockerCodes.value.includes(
        'hook_configuration_incomplete'
      )
    ) {
      return 'invalid';
    }

    if (
      !olxConfigured.value ||
      portalPreflightBlockerCodes.value.includes('hook_missing') ||
      portalPreflightBlockerCodes.value.includes('hook_disabled')
    ) {
      return 'disconnected';
    }

    if (
      portalPreflight.value?.status === 'attentionRequired' ||
      portalStatus.value?.dueReconciliationCount > 0
    ) {
      return 'degraded';
    }

    return 'configured';
  });
  const olxConnectionLabel = computed(() => {
    if (!portalStatus.value) return 'Não verificado';
    return PORTAL_CONNECTION_LABELS[portalRuntimeState.value] || 'Desconhecido';
  });
  const olxSyncStatusLabel = computed(
    () =>
      PORTAL_SYNC_STATUS_LABELS[olxSync.value?.status] ||
      olxSync.value?.status ||
      'Sem sincronização'
  );
  const olxSyncAction = computed(() =>
    olxSync.value?.status === 'published' ? 'update' : 'publish'
  );
  const olxLastSyncLabel = computed(() =>
    formatDateTime(olxSync.value?.lastSyncedAt || olxSync.value?.updatedAt)
  );
  const portalHistoryRows = computed(() =>
    normalizedPortalSyncs.value.map(sync => ({
      key: `${sync.portal}-${sync.id || sync.externalRef || sync.remoteId}`,
      portal:
        PORTAL_OPTIONS.find(option => option.value === sync.portal)?.label ||
        sync.portal?.toUpperCase() ||
        'Portal',
      status: sync.status || 'sem status',
      desiredState: sync.desiredState || 'sem estado desejado',
      observedState: sync.observedState || sync.status || 'não observado',
      lastAction: sync.lastAction || 'sem ação',
      lastError: sync.lastError || '',
      lastAttempt:
        sync.lastAttempt?.status ||
        sync.latestReconciliationAlert?.status ||
        '',
      lastSyncedAt: formatDateTime(sync.lastSyncedAt || sync.updatedAt),
    }))
  );

  const fetchPortalStatus = async () => {
    portalLoading.value = true;
    portalError.value = '';
    portalPermissionDenied.value = false;
    try {
      const { data } = await PortalIntegrationsAPI.status(selectedPortal.value);
      portalStatus.value = normalizePayload(data?.payload);
    } catch (error) {
      if (error?.response?.status === 403) {
        portalPermissionDenied.value = true;
        portalError.value =
          'Você não tem permissão para revisar integrações de portal.';
        return;
      }

      portalError.value = actionErrorMessage(
        error,
        `Não foi possível carregar o status de ${selectedPortalLabel.value}.`
      );
    } finally {
      portalLoading.value = false;
    }
  };

  const fetchPortalPreflight = async () => {
    try {
      const { data } = await PortalIntegrationsAPI.preflight(
        selectedPortal.value
      );
      portalPreflight.value = normalizePayload(data?.payload);
    } catch (error) {
      if (error?.response?.status === 403) {
        portalPermissionDenied.value = true;
        portalError.value =
          'Você não tem permissão para revisar integrações de portal.';
        return;
      }

      portalError.value = actionErrorMessage(
        error,
        `Não foi possível carregar o preflight de ${selectedPortalLabel.value}.`
      );
    }
  };

  const fetchPortalSyncs = async () => {
    if (!route.params.id) return;

    try {
      const { data } = await ImovelAPI.portalSyncs(route.params.id);
      portalSyncs.value = (data?.payload || []).map(sync =>
        normalizePayload(sync)
      );
    } catch (error) {
      portalError.value = actionErrorMessage(
        error,
        'Não foi possível carregar o histórico de sincronização do portal.'
      );
    }
  };

  const portalSyncRequest = (action, payload) => {
    if (action === 'unpublish') {
      return PortalIntegrationsAPI.unpublish(selectedPortal.value, payload);
    }

    if (action === 'update') {
      return PortalIntegrationsAPI.update(selectedPortal.value, payload);
    }

    return PortalIntegrationsAPI.publish(selectedPortal.value, payload);
  };

  const openPortalConsole = () => {
    router.push({
      name: 'imoveis_portal_integrations_console',
      params: {
        accountId: unref(sectionAccountId),
        portal: selectedPortal.value,
      },
      query: {
        imovel_id: route.params.id,
      },
    });
  };

  const openOlxAuthorizeUrl = async () => {
    if (
      !confirmSensitiveAction(
        `Abrir autorização manual de ${selectedPortalLabel.value}? Use apenas credenciais oficiais do portal.`
      )
    ) {
      return;
    }

    portalActionLoading.value = 'authorize';
    portalError.value = '';
    portalSuccessMessage.value = '';
    try {
      const { data } = await PortalIntegrationsAPI.authorizeUrl(
        selectedPortal.value
      );
      const authorizeUrl = normalizePayload(data?.payload).authorizeUrl;

      if (!authorizeUrl) {
        throw new Error('authorize_url ausente');
      }

      window.open(authorizeUrl, '_blank', 'noopener');
      portalSuccessMessage.value =
        'Fluxo de autorização manual aberto em uma nova aba.';
    } catch (error) {
      portalError.value = portalActionErrorMessage(
        error,
        `Não foi possível abrir a autorização de ${selectedPortalLabel.value}.`
      );
      useAlert(portalError.value);
    } finally {
      portalActionLoading.value = '';
    }
  };

  const runPortalSync = async action => {
    if (
      !confirmSensitiveAction(
        `Confirmar ${PORTAL_SYNC_ACTION_LABELS[action] || action} em ${selectedPortalLabel.value}? A ação ficará registrada no histórico do portal.`
      )
    ) {
      return;
    }

    portalActionLoading.value = action;
    portalError.value = '';
    portalSuccessMessage.value = '';
    try {
      const payload = {
        imovel_id: route.params.id,
        reason: unref(lifecycleReason),
        idempotency_key: `dashboard:imovel-show:${selectedPortal.value}:${action}:${route.params.id}:${Date.now()}`,
      };
      const { data } = await portalSyncRequest(action, payload);
      const sync = normalizePayload(data?.payload);
      portalSyncs.value = [
        sync,
        ...portalSyncs.value.filter(existing => existing.id !== sync.id),
      ];

      await Promise.allSettled([
        refreshImovel(),
        fetchPortalStatus(),
        fetchPortalPreflight(),
        fetchPortalSyncs(),
      ]);
      portalSuccessMessage.value =
        action === 'unpublish'
          ? `Anúncio retirado de ${selectedPortalLabel.value}.`
          : `Sincronização ${selectedPortalLabel.value} concluída.`;
      useAlert(portalSuccessMessage.value);
    } catch (error) {
      portalError.value = portalActionErrorMessage(
        error,
        `Não foi possível sincronizar com ${selectedPortalLabel.value}.`
      );
      useAlert(portalError.value);
    } finally {
      portalActionLoading.value = '';
    }
  };

  watch(selectedPortal, async (portal, previousPortal) => {
    if (!portal || portal === previousPortal) return;

    portalStatus.value = null;
    portalPreflight.value = null;
    portalError.value = '';
    portalSuccessMessage.value = '';
    await Promise.allSettled([fetchPortalStatus(), fetchPortalPreflight()]);

    if (unref(activeSection) === 'publicacao') {
      router
        .replace({
          name: route.name,
          params: route.params,
          query: { ...route.query, portal },
        })
        .catch(() => {});
    }
  });

  watch(
    () => route.query.portal,
    value => {
      const nextPortal = Array.isArray(value) ? value[0] : value;
      if (nextPortal && nextPortal !== selectedPortal.value) {
        selectedPortal.value = String(nextPortal);
      }
    }
  );

  return {
    selectedPortal,
    portalLoading,
    portalActionLoading,
    portalError,
    portalPermissionDenied,
    portalSuccessMessage,
    normalizedPortalSyncs,
    selectedPortalLabel,
    olxSync,
    olxConfigured,
    olxConnected,
    portalPreflightBlockers,
    portalPreflightWarnings,
    portalManualConfigurationRows,
    portalRuntimeState,
    olxConnectionLabel,
    olxSyncStatusLabel,
    olxSyncAction,
    olxLastSyncLabel,
    portalHistoryRows,
    fetchPortalStatus,
    fetchPortalPreflight,
    fetchPortalSyncs,
    openPortalConsole,
    openOlxAuthorizeUrl,
    runPortalSync,
  };
}

export default useImovelPortalPublication;
